/**
 * Case-scoped localStorage helpers for wizard draft state.
 *
 * Why a separate storage layer (not the existing analysis/wizard stores):
 *   - The Zustand stores persist data for the *active* case only.  When
 *     the user opens a different case the store resets and step pointers
 *     bleed across cases.
 *   - This file stores a per-case + per-phase marker so each case
 *     remembers its own "where I left off" step independently.
 *
 * Key shape: `<phase>-wizard-draft:<caseId>` → JSON `{ step, savedAt }`
 * where `<phase>` is "analysis" or "discovery".
 *
 * Failure modes are silent — localStorage can be disabled in private modes,
 * SSR contexts, or by quota.  In all such cases the helpers return `null`
 * and the wizard falls back to its default initial step.
 */

export type WizardPhase = 'analysis' | 'discovery';

const KEY_PREFIX = 'analysis-wizard-draft:';
const KEY_PREFIX_BY_PHASE: Record<WizardPhase, string> = {
  analysis: 'analysis-wizard-draft:',
  discovery: 'discovery-wizard-draft:',
};

export interface WizardDraft {
  /** The step the user was last on (0-5 for AnalysisWizard). */
  step: number;
  /** ISO timestamp when the draft was last written. */
  savedAt: string;
}

const storageKey = (caseId: string, phase: WizardPhase = 'analysis') =>
  `${KEY_PREFIX_BY_PHASE[phase]}${caseId}`;

export function readDraft(
  caseId: string | null | undefined,
  phase: WizardPhase = 'analysis',
): WizardDraft | null {
  if (!caseId) return null;
  try {
    const raw = window.localStorage.getItem(storageKey(caseId, phase));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed?.step === 'number' &&
      typeof parsed?.savedAt === 'string'
    ) {
      return parsed as WizardDraft;
    }
    return null;
  } catch {
    return null;
  }
}

export function writeDraft(
  caseId: string | null | undefined,
  step: number,
  phase: WizardPhase = 'analysis',
): WizardDraft | null {
  if (!caseId) return null;
  try {
    const draft: WizardDraft = { step, savedAt: new Date().toISOString() };
    window.localStorage.setItem(storageKey(caseId, phase), JSON.stringify(draft));
    return draft;
  } catch {
    return null;
  }
}

export function clearDraft(
  caseId: string | null | undefined,
  phase: WizardPhase = 'analysis',
): void {
  if (!caseId) return;
  try {
    window.localStorage.removeItem(storageKey(caseId, phase));
  } catch {
    // best-effort
  }
}

// Suppress unused warning — KEY_PREFIX kept as an alias to the analysis
// default for back-compat with existing imports.
void KEY_PREFIX;

/** Human-friendly relative time for the resume banner. */
export function formatRelativeTime(iso: string): string {
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diffMs = Math.max(0, now - then);
    const mins = Math.floor(diffMs / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
    const days = Math.floor(hrs / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  } catch {
    return 'recently';
  }
}
